import React, { useEffect, useState } from 'react';
import axios from 'axios';

const CONNECTION_STATES = {
    NOT_CONFIGURED: 'NOT_CONFIGURED',
    CONNECTING: 'CONNECTING',
    AUTHENTICATING: 'AUTHENTICATING',
    CONNECTED: 'CONNECTED',
    ERROR: 'ERROR'
};

export default function FleetPConnect() {
    const API_URL = process.env.REACT_APP_API_URL;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    const [connectionState, setConnectionState] = useState(CONNECTION_STATES.NOT_CONFIGURED);
    const [connectedAt, setConnectedAt] = useState(null);
    const [integration, setIntegration] = useState(null);
    const [showSettings, setShowSettings] = useState(false);

    // Form fields
    const [apiKey, setApiKey] = useState('');
    const [syncAttendance, setSyncAttendance] = useState(true);
    const [syncKmData, setSyncKmData] = useState(true);

    useEffect(() => {
        fetchIntegrationStatus();
    }, []);

    useEffect(() => {
        if (connectionState === CONNECTION_STATES.CONNECTED) {
            sessionStorage.setItem('fleetp_connected', 'true');
        } else if (connectionState === CONNECTION_STATES.NOT_CONFIGURED) {
            sessionStorage.removeItem('fleetp_connected');
        }
    }, [connectionState]);

    const fetchIntegrationStatus = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(
                `${API_URL}shypvdriverapp/api/integrations/default/FLEETP`
            );

            if (response.data.success && response.data.data) {
                const data = response.data.data;
                setIntegration(data);
                setSyncAttendance(data.syncAttendance !== false);
                setSyncKmData(data.syncKmData !== false);

                if (data.status === 'CONNECTED') {
                    setConnectionState(CONNECTION_STATES.CONNECTED);
                    setConnectedAt(data.lastConnectedAt);
                } else if (data.status === 'ERROR') {
                    setConnectionState(CONNECTION_STATES.ERROR);
                } else {
                    setConnectionState(CONNECTION_STATES.NOT_CONFIGURED);
                }
            } else {
                setConnectionState(CONNECTION_STATES.NOT_CONFIGURED);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setConnectionState(CONNECTION_STATES.NOT_CONFIGURED);
            } else {
                console.error('Error fetching integration:', err);
                setError('Failed to load integration status');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConnect = async () => {
        if (!apiKey) {
            setError('Please enter your FleetP API Key');
            return;
        }

        setSaving(true);
        setError('');
        setMsg('');

        setConnectionState(CONNECTION_STATES.CONNECTING);
        await new Promise(resolve => setTimeout(resolve, 800));

        setConnectionState(CONNECTION_STATES.AUTHENTICATING);

        try {
            const response = await axios.post(
                `${API_URL}shypvdriverapp/api/integrations/configure`,
                {
                    partnerId: 'default',
                    integrationType: 'FLEETP',
                    apiKey: apiKey,
                    syncAttendance,
                    syncKmData,
                    configuredBy: sessionStorage.getItem('user_name') || 'admin'
                }
            );

            if (response.data.success) {
                setConnectionState(CONNECTION_STATES.CONNECTED);
                setConnectedAt(new Date().toISOString());
                setMsg('FleetP connected successfully!');
                setApiKey('');
                fetchIntegrationStatus();
            } else {
                setConnectionState(CONNECTION_STATES.ERROR);
                setError(response.data.message || 'Failed to connect');
            }
        } catch (err) {
            console.error('Error configuring integration:', err);
            setConnectionState(CONNECTION_STATES.ERROR);
            setError(err.response?.data?.message || 'Failed to connect. Please check your API key.');
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Are you sure you want to disconnect FleetP? This will stop syncing attendance and KM data.')) {
            return;
        }

        setSaving(true);
        setError('');
        setMsg('');

        try {
            const response = await axios.post(
                `${API_URL}shypvdriverapp/api/integrations/default/FLEETP/disconnect`
            );

            if (response.data.success) {
                setMsg('FleetP disconnected successfully');
                setIntegration(null);
                setConnectionState(CONNECTION_STATES.NOT_CONFIGURED);
                sessionStorage.removeItem('fleetp_connected');
                setShowSettings(false);
            } else {
                setError(response.data.message || 'Failed to disconnect');
            }
        } catch (err) {
            console.error('Error disconnecting:', err);
            setError(err.response?.data?.message || 'Failed to disconnect');
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        setTesting(true);
        setError('');
        setMsg('');

        try {
            const response = await axios.post(
                `${API_URL}shypvdriverapp/api/integrations/default/FLEETP/test`
            );

            if (response.data.success) {
                setMsg('Connection test successful!');
                setConnectionState(CONNECTION_STATES.CONNECTED);
                fetchIntegrationStatus();
            } else {
                setConnectionState(CONNECTION_STATES.ERROR);
                setError(response.data.message || 'Connection test failed');
            }
        } catch (err) {
            console.error('Error testing connection:', err);
            setConnectionState(CONNECTION_STATES.ERROR);
            setError(err.response?.data?.message || 'Connection test failed');
        } finally {
            setTesting(false);
        }
    };

    const handleUpdateSyncSettings = async () => {
        setSaving(true);
        setError('');
        setMsg('');

        try {
            const response = await axios.post(
                `${API_URL}shypvdriverapp/api/integrations/configure`,
                {
                    partnerId: 'default',
                    integrationType: 'FLEETP',
                    syncAttendance,
                    syncKmData,
                    configuredBy: sessionStorage.getItem('user_name') || 'admin'
                }
            );

            if (response.data.success) {
                setMsg('Sync settings updated successfully!');
                fetchIntegrationStatus();
            } else {
                setError(response.data.message || 'Failed to update settings');
            }
        } catch (err) {
            console.error('Error updating settings:', err);
            setError(err.response?.data?.message || 'Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    const maskApiKey = (key) => {
        if (!key) return '***';
        if (key.length <= 8) return '***';
        return `${key.substring(0, 4)}...${key.substring(key.length - 4)}`;
    };

    const getConnectionDuration = () => {
        if (!connectedAt) return '';
        const now = new Date();
        const connected = new Date(connectedAt);
        const diffMs = now - connected;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffDays > 0) return `Connected ${diffDays}d ${diffHours % 24}h ago`;
        if (diffHours > 0) return `Connected ${diffHours}h ${diffMins % 60}m ago`;
        if (diffMins > 0) return `Connected ${diffMins}m ago`;
        return 'Just connected';
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px', marginRight: '10px' }}></i>
                    Loading integration status...
                </div>
            </div>
        );
    }

    // CONNECTED VIEW
    if (connectionState === CONNECTION_STATES.CONNECTED) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.title}>
                        <i className="fa fa-plug" style={{ marginRight: '10px' }}></i>
                        FleetP Integration
                    </h2>
                    <p style={styles.subtitle}>Your FleetP account is connected and syncing data</p>
                </div>

                {msg && (
                    <div style={styles.successAlert}>
                        <i className="fa fa-check-circle" style={{ marginRight: '8px' }}></i>
                        {msg}
                    </div>
                )}
                {error && (
                    <div style={styles.errorAlert}>
                        <i className="fa fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                        {error}
                    </div>
                )}

                <div style={styles.liveCard}>
                    <div style={styles.liveCardTop}>
                        <div style={styles.liveCardLeft}>
                            <div style={styles.liveBadge}>
                                <div style={styles.liveDot}></div>
                                <span style={styles.liveTextGreen}>LIVE</span>
                            </div>
                            <h3 style={styles.liveCardTitle}>FleetP Connected</h3>
                        </div>
                        <div>
                            <i className="fa fa-truck" style={{ fontSize: '32px', color: '#1976d2', opacity: 0.3 }}></i>
                        </div>
                    </div>

                    <div style={styles.liveInfoRow}>
                        <div style={styles.liveInfoItem}>
                            <i className="fa fa-key" style={{ marginRight: '6px', color: '#666' }}></i>
                            <span style={{ color: '#555', fontSize: '13px' }}>API Key: {integration?.hasApiKey ? maskApiKey('configured') : 'Not set'}</span>
                        </div>
                        <div style={styles.liveInfoItem}>
                            <i className="fa fa-clock-o" style={{ marginRight: '6px', color: '#666' }}></i>
                            <span style={{ color: '#555', fontSize: '13px' }}>{getConnectionDuration()}</span>
                        </div>
                    </div>

                    <div style={styles.syncBadges}>
                        {integration?.syncAttendance && (
                            <span style={styles.syncBadge}>
                                <i className="fa fa-check" style={{ marginRight: '4px', fontSize: '10px' }}></i>
                                Attendance Sync
                            </span>
                        )}
                        {integration?.syncKmData && (
                            <span style={styles.syncBadge}>
                                <i className="fa fa-check" style={{ marginRight: '4px', fontSize: '10px' }}></i>
                                KM Sync
                            </span>
                        )}
                    </div>

                    <button
                        onClick={() => { window.location.href = '/orders/fleetp-data'; }}
                        style={styles.viewDataButton}
                    >
                        <i className="fa fa-database" style={{ marginRight: '10px', fontSize: '18px' }}></i>
                        View Sent Data
                        <i className="fa fa-arrow-right" style={{ marginLeft: '10px' }}></i>
                    </button>

                    <div style={styles.settingsToggle}>
                        <button
                            onClick={() => setShowSettings(!showSettings)}
                            style={styles.settingsLink}
                        >
                            <i className={`fa fa-${showSettings ? 'chevron-up' : 'cog'}`} style={{ marginRight: '6px' }}></i>
                            {showSettings ? 'Hide Settings' : 'Settings'}
                        </button>
                    </div>

                    {showSettings && (
                        <div style={styles.settingsPanel}>
                            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '12px' }}>Sync Settings</h4>

                            <div style={styles.toggleRow}>
                                <label style={styles.toggleLabel}>
                                    <input type="checkbox" checked={syncAttendance} onChange={(e) => setSyncAttendance(e.target.checked)} style={styles.checkbox} />
                                    <span>Sync Attendance Data</span>
                                </label>
                            </div>

                            <div style={styles.toggleRow}>
                                <label style={styles.toggleLabel}>
                                    <input type="checkbox" checked={syncKmData} onChange={(e) => setSyncKmData(e.target.checked)} style={styles.checkbox} />
                                    <span>Sync KM Data</span>
                                </label>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
                                <button onClick={handleUpdateSyncSettings} disabled={saving} style={styles.secondaryButton}>
                                    {saving ? 'Saving...' : 'Save Settings'}
                                </button>
                                <button onClick={handleTestConnection} disabled={testing} style={styles.outlineButton}>
                                    {testing ? (
                                        <><i className="fa fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>Testing...</>
                                    ) : (
                                        <><i className="fa fa-check-circle" style={{ marginRight: '6px' }}></i>Test Connection</>
                                    )}
                                </button>
                                <button onClick={handleDisconnect} disabled={saving} style={styles.dangerButton}>
                                    <i className="fa fa-unlink" style={{ marginRight: '6px' }}></i>Disconnect
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ERROR VIEW
    if (connectionState === CONNECTION_STATES.ERROR) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.title}>
                        <i className="fa fa-plug" style={{ marginRight: '10px' }}></i>
                        FleetP Integration
                    </h2>
                    <p style={styles.subtitle}>Connection error detected</p>
                </div>

                {error && (
                    <div style={styles.errorAlert}>
                        <i className="fa fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                        {error}
                    </div>
                )}
                {msg && (
                    <div style={styles.successAlert}>
                        <i className="fa fa-check-circle" style={{ marginRight: '8px' }}></i>
                        {msg}
                    </div>
                )}

                <div style={styles.card}>
                    <div style={{ textAlign: 'center', padding: '30px 0' }}>
                        <div style={{
                            width: '80px', height: '80px', borderRadius: '50%',
                            backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', margin: '0 auto 16px'
                        }}>
                            <i className="fa fa-exclamation-triangle" style={{ fontSize: '36px', color: '#dc3545' }}></i>
                        </div>
                        <h3 style={{ fontSize: '18px', color: '#dc3545', marginBottom: '8px' }}>Connection Error</h3>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '6px' }}>
                            {integration?.errorMessage || 'FleetP connection failed. Please retry or reconfigure.'}
                        </p>
                        {integration?.hasApiKey && (
                            <p style={{ fontSize: '13px', color: '#999' }}>API Key configured</p>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                        <button onClick={handleTestConnection} disabled={testing} style={styles.primaryButton}>
                            {testing ? (
                                <><i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Retrying...</>
                            ) : (
                                <><i className="fa fa-refresh" style={{ marginRight: '8px' }}></i>Retry Connection</>
                            )}
                        </button>
                        <button onClick={handleDisconnect} disabled={saving} style={styles.dangerButton}>
                            <i className="fa fa-unlink" style={{ marginRight: '8px' }}></i>Disconnect & Reconfigure
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // NOT CONFIGURED / CONNECTING VIEW
    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>
                    <i className="fa fa-plug" style={{ marginRight: '10px' }}></i>
                    FleetP Integration
                </h2>
                <p style={styles.subtitle}>Connect your FleetP account to sync attendance and KM data automatically</p>
            </div>

            {msg && (
                <div style={styles.successAlert}>
                    <i className="fa fa-check-circle" style={{ marginRight: '8px' }}></i>
                    {msg}
                </div>
            )}
            {error && (
                <div style={styles.errorAlert}>
                    <i className="fa fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                    {error}
                </div>
            )}

            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={styles.logoContainer}>
                        <i className="fa fa-truck" style={styles.logoIcon}></i>
                    </div>
                    <h3 style={styles.cardTitle}>Connect to FleetP</h3>
                    <p style={styles.cardDescription}>
                        Enter your FleetP API Key to enable automatic synchronization of attendance and vehicle KM data.
                    </p>
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>API Key *</label>
                    <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Enter your FleetP API Key" style={styles.input} />
                </div>

                <div style={styles.checkboxGroup}>
                    <label style={styles.checkboxLabel}>
                        <input type="checkbox" checked={syncAttendance} onChange={(e) => setSyncAttendance(e.target.checked)} style={styles.checkbox} />
                        <span style={styles.checkboxText}>Enable attendance sync</span>
                    </label>
                    <p style={styles.checkboxDescription}>Automatically sync driver check-in/check-out from FleetP</p>
                </div>

                <div style={styles.checkboxGroup}>
                    <label style={styles.checkboxLabel}>
                        <input type="checkbox" checked={syncKmData} onChange={(e) => setSyncKmData(e.target.checked)} style={styles.checkbox} />
                        <span style={styles.checkboxText}>Enable KM data sync</span>
                    </label>
                    <p style={styles.checkboxDescription}>Automatically sync vehicle kilometer readings from FleetP</p>
                </div>

                <button
                    onClick={handleConnect}
                    disabled={saving || connectionState === CONNECTION_STATES.CONNECTING || connectionState === CONNECTION_STATES.AUTHENTICATING}
                    style={{ ...styles.primaryButton, opacity: saving ? 0.6 : 1 }}
                >
                    {connectionState === CONNECTION_STATES.CONNECTING ? (
                        <><i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Connecting...</>
                    ) : connectionState === CONNECTION_STATES.AUTHENTICATING ? (
                        <><i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Authenticating...</>
                    ) : (
                        <><i className="fa fa-link" style={{ marginRight: '8px' }}></i>Connect FleetP</>
                    )}
                </button>

                {(connectionState === CONNECTION_STATES.CONNECTING || connectionState === CONNECTION_STATES.AUTHENTICATING) && (
                    <div style={styles.connectionProgress}>
                        <div style={styles.progressSteps}>
                            <div style={{
                                ...styles.progressStep,
                                ...((connectionState === CONNECTION_STATES.CONNECTING || connectionState === CONNECTION_STATES.AUTHENTICATING) ? styles.progressStepActive : {})
                            }}>
                                <div style={styles.stepIcon}>1</div>
                                <span>Connecting</span>
                            </div>
                            <div style={styles.progressLine}></div>
                            <div style={{
                                ...styles.progressStep,
                                ...(connectionState === CONNECTION_STATES.AUTHENTICATING ? styles.progressStepActive : {})
                            }}>
                                <div style={styles.stepIcon}>2</div>
                                <span>Authenticating</span>
                            </div>
                            <div style={styles.progressLine}></div>
                            <div style={styles.progressStep}>
                                <div style={styles.stepIcon}>3</div>
                                <span>Connected</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: { padding: '20px', maxWidth: '800px', margin: '0 auto' },
    header: { marginBottom: '24px' },
    title: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px 0', display: 'flex', alignItems: 'center' },
    subtitle: { fontSize: '14px', color: '#666', margin: 0 },
    loadingContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#666' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '24px' },
    cardHeader: { textAlign: 'center', marginBottom: '24px' },
    logoContainer: { width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' },
    logoIcon: { fontSize: '36px', color: '#1976d2' },
    cardTitle: { fontSize: '20px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px 0' },
    cardDescription: { fontSize: '14px', color: '#666', margin: 0, lineHeight: '1.5' },
    formGroup: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' },
    input: { width: '100%', padding: '12px 14px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' },
    checkboxGroup: { marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' },
    checkboxLabel: { display: 'flex', alignItems: 'center', cursor: 'pointer' },
    checkbox: { width: '18px', height: '18px', marginRight: '10px', cursor: 'pointer' },
    checkboxText: { fontSize: '14px', fontWeight: '500', color: '#333' },
    checkboxDescription: { fontSize: '12px', color: '#666', margin: '6px 0 0 28px' },
    primaryButton: { width: '100%', padding: '14px 20px', fontSize: '15px', fontWeight: '500', color: '#fff', backgroundColor: '#1976d2', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '8px' },
    secondaryButton: { padding: '10px 16px', fontSize: '14px', fontWeight: '500', color: '#fff', backgroundColor: '#28a745', border: 'none', borderRadius: '6px', cursor: 'pointer' },
    outlineButton: { padding: '10px 16px', fontSize: '14px', fontWeight: '500', color: '#1976d2', backgroundColor: '#fff', border: '1px solid #1976d2', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
    dangerButton: { padding: '10px 16px', fontSize: '14px', fontWeight: '500', color: '#fff', backgroundColor: '#dc3545', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
    successAlert: { padding: '12px 16px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', display: 'flex', alignItems: 'center' },
    errorAlert: { padding: '12px 16px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', display: 'flex', alignItems: 'center' },
    liveCard: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '28px', border: '2px solid #dcfce7' },
    liveCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
    liveCardLeft: { display: 'flex', flexDirection: 'column', gap: '8px' },
    liveBadge: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#dcfce7', borderRadius: '20px', alignSelf: 'flex-start' },
    liveDot: { width: '10px', height: '10px', backgroundColor: '#22c55e', borderRadius: '50%', animation: 'pulse 2s infinite', boxShadow: '0 0 0 0 rgba(34, 197, 94, 0.7)' },
    liveTextGreen: { color: '#166534', fontWeight: 'bold', fontSize: '11px', textTransform: 'uppercase' },
    liveCardTitle: { fontSize: '22px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
    liveInfoRow: { display: 'flex', gap: '20px', marginBottom: '14px', flexWrap: 'wrap' },
    liveInfoItem: { display: 'flex', alignItems: 'center' },
    syncBadges: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
    syncBadge: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', fontSize: '12px', color: '#166534' },
    viewDataButton: { width: '100%', padding: '16px 24px', fontSize: '16px', fontWeight: '600', color: '#fff', backgroundColor: '#1976d2', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s', marginBottom: '16px' },
    settingsToggle: { textAlign: 'center', borderTop: '1px solid #eee', paddingTop: '14px' },
    settingsLink: { background: 'none', border: 'none', color: '#666', fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
    settingsPanel: { marginTop: '16px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' },
    toggleRow: { marginBottom: '12px' },
    toggleLabel: { display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#333' },
    connectionProgress: { marginTop: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' },
    progressSteps: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    progressStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', opacity: 0.5, fontSize: '12px', color: '#666' },
    progressStepActive: { opacity: 1, color: '#1976d2' },
    stepIcon: { width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', color: '#1976d2' },
    progressLine: { flex: 1, height: '2px', backgroundColor: '#ddd', margin: '0 10px' },
};

// Add CSS animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
@keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
    100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}
@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;
document.head.appendChild(styleSheet);
